import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { TooltipProvider } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/browser/use-is-mobile';
import { cn } from '@/lib/utils';
import { useSidebarPreferenceStore } from '@/store/sidebar-preference/use-sidebar-preference-store';

import { SIDEBAR_STATE, SIDEBAR_USER_PREFERENCE } from './types/sidebar-enums';
import type { SidebarContextValue } from './types/sidebar-provider-variants-types';
import { SIDEBAR_KEYBOARD_SHORTCUT, SIDEBAR_WIDTH, SIDEBAR_WIDTH_ICON } from './constants/sidebar-provider-variants-constants';
import { SidebarContext } from './sidebar-context';

const SIDEBAR_COOKIE_NAME = 'sidebar:state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const DASHBOARD_AUTO_COLLAPSE_WIDTH = 1440;
const DEFAULT_AUTO_COLLAPSE_WIDTH = 1200;
const SIDEBAR_FORCE_COLLAPSE_WIDTH = 1024;

function getAutoCollapseWidth(pathname: string): number {
  return pathname.startsWith('/board') ? DASHBOARD_AUTO_COLLAPSE_WIDTH : DEFAULT_AUTO_COLLAPSE_WIDTH;
}

export interface SidebarProviderProps extends React.ComponentProps<'div'> {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const SidebarProvider = React.forwardRef<HTMLDivElement, SidebarProviderProps>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile();
    const location = useLocation();
    const [openMobile, setOpenMobile] = React.useState(false);

    const { preference: userPreference, setPreference: setStorePreference } = useSidebarPreferenceStore(
      useShallow(s => ({
        preference: s.preference,
        setPreference: s.setPreference,
      }))
    );

    const [_open, _setOpen] = React.useState(defaultOpen);
    const open = openProp ?? _open;

    const applyOpenState = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState = typeof value === 'function' ? value(open) : value;
        if (setOpenProp) {
          setOpenProp(openState);
        } else {
          _setOpen(openState);
        }
        document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
      },
      [setOpenProp, open]
    );

    const setOpen = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState = typeof value === 'function' ? value(open) : value;
        const preference: SIDEBAR_USER_PREFERENCE = openState ? SIDEBAR_USER_PREFERENCE.EXPANDED : SIDEBAR_USER_PREFERENCE.COLLAPSED;
        setStorePreference(preference);
        applyOpenState(openState);
      },
      [applyOpenState, open, setStorePreference]
    );

    const toggleSidebar = React.useCallback(() => {
      return isMobile ? setOpenMobile(open => !open) : setOpen(open => !open);
    }, [isMobile, setOpen, setOpenMobile]);

    React.useEffect(() => {
      if (isMobile) return undefined;

      let frameId: number | null = null;

      const applyAutoState = () => {
        if (window.innerWidth < SIDEBAR_FORCE_COLLAPSE_WIDTH) {
          applyOpenState(false);
          return;
        }

        if (userPreference) {
          applyOpenState(userPreference === 'expanded');
          return;
        }

        const threshold = getAutoCollapseWidth(location.pathname);
        applyOpenState(window.innerWidth >= threshold);
      };

      const handleResize = () => {
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
        }
        frameId = requestAnimationFrame(applyAutoState);
      };

      applyAutoState();
      window.addEventListener('resize', handleResize);

      return () => {
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
        }
        window.removeEventListener('resize', handleResize);
      };
    }, [applyOpenState, isMobile, location.pathname, userPreference]);

    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [toggleSidebar]);

    const state: SIDEBAR_STATE = open ? SIDEBAR_STATE.EXPANDED : SIDEBAR_STATE.COLLAPSED;

    const contextValue = React.useMemo<SidebarContextValue>(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
      }),
      [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <TooltipProvider delayDuration={0}>
          <div
            style={
              {
                '--sidebar-width': SIDEBAR_WIDTH,
                '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
                ...style,
              } as React.CSSProperties
            }
            className={cn(
              'group/sidebar-wrapper has-[[data-variant=inset]]:bg-sidebar flex min-h-svh w-full',
              className
            )}
            ref={ref}
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarContext.Provider>
    );
  }
);
SidebarProvider.displayName = 'SidebarProvider';
